import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { CenteredFullScreenLayout } from '@/components/layout'
import { useAuth } from '@/contexts'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from 'sonner'

export default function SignInPage() {
  const searchParams = new URLSearchParams(window.location.search)
  const redirectUrl = searchParams.get('redirect_url') || '/home'

  const { signIn, signInWithOtp, signUp } = useAuth()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isMagicLinkMode, setIsMagicLinkMode] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [magicLinkSent, setMagicLinkSent] = useState(false)

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    const { error } = await signIn(email, password)

    if (error) {
      toast.error(error.message)
      setIsLoading(false)
      return
    }

    toast.success('Signed in successfully!')
    navigate(redirectUrl)
  }

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    const { error } = await signUp(email, password)

    if (error) {
      toast.error(error.message)
      setIsLoading(false)
      return
    }

    toast.success('Account created! Check your email for confirmation.')
    setIsLoading(false)
  }

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    const { error } = await signInWithOtp(email)

    if (error) {
      toast.error(error.message)
      setIsLoading(false)
      return
    }

    setMagicLinkSent(true)
    setIsLoading(false)
    toast.success('Magic link sent! Check your email.')
  }

  return (
    <CenteredFullScreenLayout>
      <Card className="mx-4 w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-2xl text-center">
            {isMagicLinkMode ? 'Magic Link Sign In' : 'Sign In'}
          </CardTitle>
          <CardDescription className="text-center">
            {magicLinkSent
              ? `A magic link has been sent to ${email}`
              : isMagicLinkMode
                ? "We'll send a link to your email"
                : 'Enter your credentials to continue'
            }
          </CardDescription>
        </CardHeader>

        {magicLinkSent ? (
          <>
            <CardContent className="text-center">
              <CardDescription>
                A magic link has been sent to {email}. Check your inbox and click the link to sign in.
              </CardDescription>
            </CardContent>
            <CardFooter className="flex-col gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setMagicLinkSent(false)
                  setIsMagicLinkMode(false)
                }}
                className="w-full"
              >
                Back to sign in
              </Button>
            </CardFooter>
          </>
        ) : (
          <form onSubmit={isMagicLinkMode ? handleMagicLink : handleSignIn} className="contents">
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              {!isMagicLinkMode && (
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required={!isMagicLinkMode}
                  />
                  <div className="text-right">
                    <a
                      href="/forgot-password"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors hover:underline"
                    >
                      Forgot password?
                    </a>
                  </div>
                </div>
              )}
            </CardContent>

            <CardFooter className="flex-col gap-2">
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading
                  ? 'Loading...'
                  : isMagicLinkMode
                    ? 'Send Magic Link'
                    : 'Sign In'
                }
              </Button>

              {!isMagicLinkMode && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSignUp}
                  disabled={isLoading || !password}
                  className="w-full"
                >
                  {isLoading ? 'Creating account...' : 'Create Account'}
                </Button>
              )}

              <button
                type="button"
                onClick={() => {
                  setIsMagicLinkMode(!isMagicLinkMode)
                  setMagicLinkSent(false)
                }}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors hover:underline"
              >
                {isMagicLinkMode ? 'Sign in with password' : 'Sign in with magic link'}
              </button>
            </CardFooter>
          </form>
        )}
      </Card>
    </CenteredFullScreenLayout>
  )
}
